import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import { COLORS, Panel, Table, Badge, Button, Field, inputStyle, Banner, StatTile, Spinner, dateTime } from '../components/ui';

/**
 * Bulk issue / modify.
 *
 *   filter -> preview the exact diff -> explicit confirm -> execute in batches
 *          -> verify each row from SQL -> per-row result -> audit record
 *
 * There is deliberately no one-click "issue all". Every step below exists so a
 * human sees exactly what will change before anything is sent.
 */

const EMPTY_FILTER = {
    pmpks: [], statuses: ['REQUESTED'], repairCenters: [], sites: '',
    accounts: [], types: [], targetFrom: '', targetTo: '',
    requestedFrom: '', requestedTo: '', classification: '', limit: 500,
};

function MultiSelect({ options, value, onChange, size = 5, getKey, getLabel }) {
    return (
        <select multiple size={size} value={value.map(String)}
            onChange={e => onChange([...e.target.selectedOptions].map(o => o.value))}
            style={{ ...inputStyle, minWidth: 200, height: 'auto' }}>
            {options.map(o => (
                <option key={getKey(o)} value={getKey(o)}>{getLabel(o)}</option>
            ))}
        </select>
    );
}

export default function BulkPage() {
    const [meta, setMeta] = useState(null);
    const [lookups, setLookups] = useState(null);
    const [filter, setFilter] = useState(EMPTY_FILTER);
    const [operation, setOperation] = useState('issue');
    const [value, setValue] = useState('');
    const [dryRun, setDryRun] = useState(true);

    const [preview, setPreview] = useState(null);
    const [selected, setSelected] = useState(new Set());
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [confirming, setConfirming] = useState(false);
    const [typedCount, setTypedCount] = useState('');
    const [run, setRun] = useState(null);
    const poll = useRef(null);

    useEffect(() => {
        api.bulkOperations().then(setMeta).catch(e => setError(e.message));
        api.pmLookups().then(setLookups).catch(e => setError(e.message));
        return () => clearInterval(poll.current);
    }, []);

    const op = meta?.operations.find(o => o.key === operation);
    const changeRows = preview ? preview.rows.filter(r => r.action === 'change') : [];
    const selectedCount = changeRows.filter(r => selected.has(r.wopk)).length;

    const set = (k, v) => setFilter(f => ({ ...f, [k]: v }));

    const doPreview = useCallback(async () => {
        setLoading(true); setError(null); setRun(null); setConfirming(false);
        try {
            const body = {
                filter: { ...filter, sites: filter.sites ? filter.sites.split(/[\s,;]+/).filter(Boolean) : [] },
                operation, value,
            };
            const p = await api.bulkPreview(body);
            setPreview(p);
            setSelected(new Set(p.rows.filter(r => r.action === 'change').map(r => r.wopk)));
        } catch (e) { setError(e.message); setPreview(null); }
        finally { setLoading(false); }
    }, [filter, operation, value]);

    const pollRun = useCallback((runId) => {
        clearInterval(poll.current);
        poll.current = setInterval(async () => {
            try {
                const r = await api.bulkRun(runId);
                setRun(r);
                if (['complete', 'error', 'canceled'].includes(r.status)) clearInterval(poll.current);
            } catch { clearInterval(poll.current); }
        }, 800);
    }, []);

    const execute = useCallback(async () => {
        setLoading(true); setError(null);
        try {
            const body = {
                filter: { ...filter, sites: filter.sites ? filter.sites.split(/[\s,;]+/).filter(Boolean) : [] },
                operation, value, dryRun,
                wopks: changeRows.filter(r => selected.has(r.wopk)).map(r => r.wopk),
                confirmedCount: selectedCount,
            };
            const started = await api.bulkExecute(body);
            setRun(started);
            setConfirming(false);
            setTypedCount('');
            pollRun(started.runId);
        } catch (e) { setError(e.message); }
        finally { setLoading(false); }
    }, [filter, operation, value, dryRun, changeRows, selected, selectedCount, pollRun]);

    const needsTypedConfirm = selectedCount >= 25;
    const canConfirm = !needsTypedConfirm || String(typedCount) === String(selectedCount);

    return (
        <div>
            <h1 style={{ fontSize: 22, margin: '0 0 4px', color: COLORS.ink }}>Bulk issue / modify</h1>
            <p style={{ color: COLORS.ink2, fontSize: 13, marginTop: 0 }}>
                Filter, check the diff, confirm, then execute. Every row is verified from the database afterwards
                and written to the audit trail. <Link to="/bulk/history" style={{ color: COLORS.link }}>Run history</Link>
            </p>

            {meta && (
                <Banner tone={meta.environment === 'entUNITED' ? 'danger' : 'warn'}>
                    Target: <strong>{meta.environment}</strong> — this is MC PRODUCTION.
                    Cap {meta.maxRowsPerRun} rows per run. Not available here: {meta.outOfScope.join(', ')}.
                </Banner>
            )}
            {meta && (
                <Banner tone={meta.source === 'mc-sql' ? 'info' : 'warn'}>
                    Reading and verifying via <strong>{meta.sourceLabel}</strong>. {meta.verificationNote}
                    {!meta.supportsAssetFilters && ' Asset classification filtering and the retired-asset exclusion are unavailable on this path.'}
                </Banner>
            )}
            {error && <Banner tone="danger">{error}</Banner>}

            <Panel title="1. Select work orders" subtitle="Filtering happens in SQL — the MC API ignores plain querystring filters">
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
                    <Field label="PM programme" hint="blank = any">
                        <MultiSelect options={lookups?.pms || []} value={filter.pmpks}
                            onChange={v => set('pmpks', v)} getKey={o => o.PMPK}
                            getLabel={o => `${o.PMID} (${o.Assets})`} size={6} />
                    </Field>
                    <Field label="Status">
                        <MultiSelect options={lookups?.statuses || []} value={filter.statuses}
                            onChange={v => set('statuses', v)} getKey={o => o.Status}
                            getLabel={o => `${o.Status} (${o.Uses})`} size={6} />
                    </Field>
                    <Field label="Repair centre / state">
                        <MultiSelect options={lookups?.centres || []} value={filter.repairCenters}
                            onChange={v => set('repairCenters', v)} getKey={o => o.RepairCenterID}
                            getLabel={o => o.RepairCenterID} size={6} />
                    </Field>
                    <Field label="GL account">
                        <MultiSelect options={lookups?.accounts || []} value={filter.accounts}
                            onChange={v => set('accounts', v)} getKey={o => o.AccountID}
                            getLabel={o => `${o.AccountID} ${o.AccountName}`} size={6} />
                    </Field>
                    <Field label="Sites" hint="paste 4-digit codes, any separator" width={180}>
                        <textarea rows={5} value={filter.sites} onChange={e => set('sites', e.target.value)}
                            placeholder="4411 4472 4439" style={{ ...inputStyle, resize: 'vertical' }} />
                    </Field>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        <Field label="Target date from"><input type="date" value={filter.targetFrom} onChange={e => set('targetFrom', e.target.value)} style={inputStyle} /></Field>
                        <Field label="Target date to"><input type="date" value={filter.targetTo} onChange={e => set('targetTo', e.target.value)} style={inputStyle} /></Field>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        <Field label="Requested from"><input type="date" value={filter.requestedFrom} onChange={e => set('requestedFrom', e.target.value)} style={inputStyle} /></Field>
                        <Field label="Asset classification" hint="e.g. AC, CP">
                            <input value={filter.classification} onChange={e => set('classification', e.target.value)} style={inputStyle} placeholder="AC" />
                        </Field>
                    </div>
                </div>
            </Panel>

            <Panel title="2. Choose the change">
                <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                    <Field label="Operation">
                        <select value={operation} onChange={e => { setOperation(e.target.value); setValue(''); setPreview(null); }} style={{ ...inputStyle, minWidth: 240 }}>
                            {(meta?.operations || []).map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
                        </select>
                    </Field>

                    {op?.takesValue && (
                        <Field label="New value" hint={operation === 'set-target-date' ? 'YYYY-MM-DD' : null}>
                            {operation === 'set-target-date' ? (
                                <input type="date" value={value} onChange={e => setValue(e.target.value)} style={inputStyle} />
                            ) : operation === 'set-account' ? (
                                <select value={value} onChange={e => setValue(e.target.value)} style={{ ...inputStyle, minWidth: 260 }}>
                                    <option value="">Select…</option>
                                    {(lookups?.accounts || []).map(a => <option key={a.AccountID} value={a.AccountID}>{a.AccountID} {a.AccountName}</option>)}
                                </select>
                            ) : operation === 'set-priority' ? (
                                <select value={value} onChange={e => setValue(e.target.value)} style={{ ...inputStyle, minWidth: 260 }}>
                                    <option value="">Select…</option>
                                    {(lookups?.priorities || []).map(p => <option key={p.Priority} value={p.Priority}>{p.Priority} — {p.PriorityDesc}</option>)}
                                </select>
                            ) : operation === 'assign-supervisor' ? (
                                <select value={value} onChange={e => setValue(e.target.value)} style={{ ...inputStyle, minWidth: 260 }}>
                                    <option value="">Select…</option>
                                    {(lookups?.supervisors || []).map(s => <option key={s.LaborID} value={s.LaborID}>{s.LaborName}</option>)}
                                </select>
                            ) : (
                                <input value={value} onChange={e => setValue(e.target.value)} style={inputStyle} />
                            )}
                        </Field>
                    )}

                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: COLORS.ink }}>
                        <input type="checkbox" checked={dryRun} onChange={e => setDryRun(e.target.checked)} />
                        Dry run — write the audit trail, send nothing to MC
                    </label>

                    <Button kind="primary" onClick={doPreview} disabled={loading}>Preview the diff</Button>
                </div>
            </Panel>

            {loading && !preview && <Spinner label="Reading MC" />}

            {preview && (
                <Panel
                    title="3. Preview"
                    subtitle={`${preview.operationLabel} → ${preview.valueDisplay} · ${preview.environment}`}
                    right={<span style={{ fontSize: 12, color: COLORS.ink2 }}>{selectedCount} of {changeRows.length} changes selected</span>}
                >
                    <div style={{ display: 'flex', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
                        <StatTile label="Matched" value={preview.matched} />
                        <StatTile label="Will change" value={preview.willChange} tone={preview.willChange ? 'good' : undefined} />
                        <StatTile label="Already set" value={preview.counts['skipped-already-set'] || 0} sub="skipped, not rewritten" />
                        <StatTile label="Not eligible" value={preview.counts['not-eligible'] || 0} sub="wrong status for this op" />
                        <StatTile label="Blocked" value={preview.counts.blocked || 0} sub="closed or canceled" tone={preview.counts.blocked ? 'warn' : undefined} />
                    </div>

                    {preview.overCap && <Banner tone="danger">Over the {preview.maxRowsPerRun}-row cap. Narrow the filter and run it in waves.</Banner>}
                    {(preview.warnings || []).map((w, i) => <Banner key={i} tone="warn">{w}</Banner>)}

                    <div style={{ marginBottom: 8, display: 'flex', gap: 8 }}>
                        <Button onClick={() => setSelected(new Set(changeRows.map(r => r.wopk)))}>Select all changes</Button>
                        <Button onClick={() => setSelected(new Set())}>Select none</Button>
                    </div>

                    <Table
                        rowKey={r => r.wopk}
                        columns={[
                            {
                                key: 'sel', label: '', render: r => r.action === 'change' ? (
                                    <input type="checkbox" checked={selected.has(r.wopk)} onChange={e => {
                                        const next = new Set(selected);
                                        e.target.checked ? next.add(r.wopk) : next.delete(r.wopk);
                                        setSelected(next);
                                    }} />
                                ) : null
                            },
                            { key: 'woid', label: 'WO', render: r => <Link to={`/wo/${r.wopk}`} style={{ color: COLORS.link }}>{r.woid}</Link> },
                            { key: 'site', label: 'Site', render: r => `${r.site || ''} ${r.siteName || ''}`.trim() },
                            { key: 'asset', label: 'Asset' },
                            { key: 'status', label: 'Status' },
                            { key: 'field', label: 'Field' },
                            { key: 'diff', label: 'Current → New', render: r => r.action === 'change'
                                ? <span><span style={{ color: COLORS.ink2 }}>{r.current || '(blank)'}</span> <span style={{ color: COLORS.muted }}>→</span> <strong>{r.target}</strong></span>
                                : <span style={{ color: COLORS.muted }}>{r.current || '(blank)'}</span> },
                            { key: 'action', label: 'Action', render: r => <Badge value={r.action} title={r.reason} /> },
                            { key: 'reason', label: 'Why not', wrap: true, render: r => <span style={{ color: COLORS.muted }}>{r.reason || ''}</span> },
                        ]}
                        rows={preview.rows}
                    />

                    <div style={{ marginTop: 16, display: 'flex', gap: 12, alignItems: 'center' }}>
                        <Button kind={dryRun ? 'primary' : 'danger'} disabled={selectedCount === 0 || preview.overCap}
                            onClick={() => setConfirming(true)}>
                            {dryRun ? `Dry run ${selectedCount} rows` : `Apply to ${selectedCount} work orders`}
                        </Button>
                        {!dryRun && <span style={{ fontSize: 12.5, color: COLORS.critical }}>This writes to {preview.environment}.</span>}
                    </div>
                </Panel>
            )}

            {confirming && (
                <div style={{
                    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 100,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    <div style={{ background: '#fff', borderRadius: 10, padding: 24, width: 520, maxWidth: '92vw' }}>
                        <h2 style={{ margin: '0 0 8px', fontSize: 17 }}>
                            {dryRun ? 'Confirm dry run' : 'Confirm change to production'}
                        </h2>
                        <p style={{ fontSize: 13.5, color: COLORS.ink2, lineHeight: 1.5 }}>
                            {preview.operationLabel} → <strong>{preview.valueDisplay}</strong><br />
                            <strong>{selectedCount}</strong> work orders in <strong>{preview.environment}</strong>
                            {dryRun
                                ? ' — nothing will be sent to MC; the audit trail is written as a dry run.'
                                : ' — this changes live work orders.'}
                        </p>

                        {needsTypedConfirm && (
                            <div style={{ marginTop: 12 }}>
                                <Field label={`Type ${selectedCount} to proceed`}>
                                    <input value={typedCount} onChange={e => setTypedCount(e.target.value)}
                                        style={{ ...inputStyle, width: 120 }} autoFocus />
                                </Field>
                            </div>
                        )}

                        <div style={{ display: 'flex', gap: 10, marginTop: 20, justifyContent: 'flex-end' }}>
                            <Button onClick={() => { setConfirming(false); setTypedCount(''); }}>Cancel</Button>
                            <Button kind={dryRun ? 'primary' : 'danger'} disabled={!canConfirm || loading} onClick={execute}>
                                {dryRun ? 'Run dry run' : `Yes — change ${selectedCount} work orders`}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {run && (
                <Panel
                    title={`4. Result — ${run.dryRun ? 'dry run' : 'applied'}`}
                    subtitle={`run ${run.runId} · ${run.performedBy} · started ${dateTime(run.startedAt)} · verified via ${run.sourceLabel || run.source}`}
                    right={run.status === 'complete'
                        ? <a href={api.bulkCsvUrl(run.runId)} style={{ color: COLORS.link, fontSize: 13 }}>Download CSV</a>
                        : <span style={{ fontSize: 12, color: COLORS.ink2 }}>{run.status}… {run.processed}/{run.total}</span>}
                >
                    {run.status === 'running' || run.status === 'verifying' ? (
                        <div style={{ marginBottom: 12 }}>
                            <div style={{ height: 8, background: COLORS.grid, borderRadius: 4, overflow: 'hidden' }}>
                                <div style={{ width: `${Math.round((run.processed / Math.max(run.total, 1)) * 100)}%`, height: '100%', background: COLORS.link }} />
                            </div>
                            <div style={{ fontSize: 12, color: COLORS.ink2, marginTop: 6 }}>
                                {run.status === 'verifying' ? 'Re-reading every touched row from SQL…' : `${run.processed} of ${run.total}`}
                                {' · '}<Button kind="ghost" onClick={() => api.bulkCancel(run.runId)}>Stop after this batch</Button>
                            </div>
                        </div>
                    ) : null}

                    <div style={{ display: 'flex', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
                        {Object.entries(run.counts || {}).map(([k, v]) => (
                            <StatTile key={k} label={k} value={v}
                                tone={k === 'failed' || k === 'unverified' ? 'bad' : k === 'updated' ? 'good' : undefined} />
                        ))}
                        {!run.dryRun && <StatTile label="Verified from SQL" value={run.verifiedCount} sub="re-read after the write" />}
                    </div>

                    {run.unverified?.length > 0 && (
                        <Banner tone="danger">
                            {run.unverified.length} work order(s) returned success from MC but did not actually change in the database.
                            These are listed below as <strong>unverified</strong> — treat them as not done.
                        </Banner>
                    )}
                    {run.auditErrors?.length > 0 && (
                        <Banner tone="warn">{run.auditErrors.length} audit row(s) failed to write — the MC change still happened. See server log.</Banner>
                    )}
                    {run.error && <Banner tone="danger">{run.error}</Banner>}

                    {run.rows && (
                        <Table
                            rowKey={r => r.wopk}
                            columns={[
                                { key: 'woid', label: 'WO', render: r => <Link to={`/wo/${r.wopk}`} style={{ color: COLORS.link }}>{r.woid}</Link> },
                                { key: 'site', label: 'Site' },
                                { key: 'field', label: 'Field' },
                                { key: 'current', label: 'Was' },
                                { key: 'target', label: 'Set to' },
                                { key: 'outcome', label: 'Outcome', render: r => <Badge value={r.outcome} /> },
                                { key: 'verified', label: 'Verified', render: r => r.verified ? 'yes' : (r.outcome === 'updated' ? 'no' : '') },
                                { key: 'verifiedValue', label: 'DB now holds' },
                                { key: 'error', label: 'Detail', wrap: true, render: r => <span style={{ color: COLORS.muted }}>{r.error || ''}</span> },
                            ]}
                            rows={run.rows}
                        />
                    )}
                </Panel>
            )}
        </div>
    );
}
